import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as ReactivateIcon,
  MarkEmailRead,
  SendOutlined,
  CheckCircleOutline,
  HourglassEmpty,
} from '@mui/icons-material';
import {
  getTeachersDirect,
  deleteTeacherDirect,
  reactivateTeacherDirect,
  hasTeacherTimetableRecords,
  sendInviteDirect,
} from '@timetable/api';
import type { TeacherWithRelations } from '@timetable/types';
import type { SortConfig } from '../shared';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import { ConfirmDialog } from '../ConfirmDialog';
import { TablePagination } from '../shared';
import { useToast } from '../Toast';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../lib/queryKeys';

interface TeacherListProps {
  searchQuery: string;
  selectedFilters: string[];
  sortConfig: SortConfig;
  showInactive?: boolean;
  onEditClick: (teacher: TeacherWithRelations) => void;
  /** School name used in invite emails */
  schoolName?: string;
}

export function TeacherList({ 
  searchQuery, 
  selectedFilters,
  sortConfig,
  showInactive = false,
  onEditClick,
  schoolName,
}: TeacherListProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<TeacherWithRelations | null>(null);
  const [teacherHasTimetableRecords, setTeacherHasTimetableRecords] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  const { schoolContext } = useAuthStore();

  const { data: teachers, isLoading, error } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '', includeInactive: showInactive }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '', includeInactive: showInactive }),
    enabled: !!schoolContext,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTeacherDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teachers.all });
      const msg = teacherHasTimetableRecords ? 'Teacher deactivated successfully' : 'Teacher deleted successfully';
      showToast(msg, 'success');
      setDeleteDialogOpen(false);
      setTeacherToDelete(null);
      setTeacherHasTimetableRecords(false);
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete teacher', 'error');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateTeacherDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teachers.all });
      showToast('Teacher reactivated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to reactivate teacher', 'error');
    },
  });

  // Filter, sort, and paginate teachers
  const processedTeachers = useMemo(() => {
    if (!teachers) return [];

    let filtered = [...teachers];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((teacher) => {
        const nameMatch = teacher.name.toLowerCase().includes(query);
        const emailMatch = teacher.email?.toLowerCase().includes(query);
        const subjectMatch = teacher.subjects?.some((subject) =>
          subject.subject_name.toLowerCase().includes(query)
        );
        return nameMatch || emailMatch || subjectMatch;
      });
    }

    // Apply type filters
    if (selectedFilters.length > 0) {
      filtered = filtered.filter((teacher) => {
        const teacherType = teacher.teacher_type?.type_name?.toLowerCase();
        return teacherType && selectedFilters.includes(teacherType);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortConfig.field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'teacher_type':
          aValue = (a.teacher_type?.type_name || '').toLowerCase();
          bValue = (b.teacher_type?.type_name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [teachers, searchQuery, selectedFilters, sortConfig]);

  // Paginate
  const paginatedTeachers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedTeachers.slice(startIndex, startIndex + rowsPerPage);
  }, [processedTeachers, page, rowsPerPage]);

  const handleDeleteClick = async (teacher: TeacherWithRelations) => {
    setTeacherToDelete(teacher);
    const hasTimetableRecords = await hasTeacherTimetableRecords(teacher.id);
    setTeacherHasTimetableRecords(hasTimetableRecords);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (teacherToDelete) {
      deleteMutation.mutate(teacherToDelete.id);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSendInvite = async (teacher: TeacherWithRelations) => {
    if (!teacher.email || !schoolContext) return;
    setSendingInvite(teacher.id);
    try {
      const result = await sendInviteDirect({
        email: teacher.email,
        role: 'teacher',
        schoolId: schoolContext,
        schoolName: schoolName ?? schoolContext,
        name: teacher.name,
        refId: teacher.id,
      });
      if (result.emailExists) {
        showToast(`${teacher.email} already has an account`, 'info');
      } else {
        showToast(`Invite sent to ${teacher.email}`, 'success');
      }
    } catch {
      showToast('Failed to send invite', 'error');
    } finally {
      setSendingInvite(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Failed to load teachers</Typography>
      </Box>
    );
  }

  if (processedTeachers.length === 0) {
    return (
      <EmptyState
        title="No teachers found"
        description={
          searchQuery || selectedFilters.length > 0
            ? 'Try adjusting your search or filter criteria'
            : 'Get started by adding your first teacher'
        }
      />
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Subjects</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTeachers.map((teacher) => (
              <TableRow key={teacher.id} hover sx={!teacher.is_active ? { opacity: 0.5 } : undefined}>
                <TableCell>{teacher.name}</TableCell>
                <TableCell>{teacher.email || '-'}</TableCell>
                <TableCell>{teacher.phone_number || '-'}</TableCell>
                <TableCell>
                  {teacher.teacher_type?.type_name || '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {teacher.subjects && teacher.subjects.length > 0 ? (
                      teacher.subjects.map((subject) => (
                        <Chip
                          key={subject.id}
                          label={subject.subject_name}
                          size="small"
                          sx={{
                            backgroundColor: subject.color_code || undefined,
                            color: subject.color_code ? '#fff' : undefined,
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No subjects assigned
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {(teacher as any).user_id ? (
                    <Tooltip title="Account activated">
                      <Chip
                        icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : teacher.email ? (
                    <Tooltip title="Invite not yet accepted">
                      <Chip
                        icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
                        label="Pending"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Chip label="No email" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  {teacher.is_active === false ? (
                    <Tooltip title="Reactivate teacher">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => reactivateMutation.mutate(teacher.id)}
                        aria-label="reactivate teacher"
                      >
                        <ReactivateIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      {/* Send / Resend invite — only when email exists and account not yet activated */}
                      {teacher.email && !(teacher as any).user_id && (
                        <Tooltip title={`Send invite to ${teacher.email}`}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSendInvite(teacher)}
                              disabled={sendingInvite === teacher.id}
                              aria-label="send invite"
                            >
                              {sendingInvite === teacher.id
                                ? <CircularProgress size={16} />
                                : <SendOutlined fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {/* Already activated — show indicator */}
                      {(teacher as any).user_id && (
                        <Tooltip title="Account activated — no invite needed">
                          <IconButton size="small" disabled aria-label="account active">
                            <MarkEmailRead fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onEditClick(teacher)}
                        aria-label="edit teacher"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(teacher)}
                        aria-label="delete teacher"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          count={processedTeachers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={teacherHasTimetableRecords ? 'Deactivate Teacher' : 'Delete Teacher'}
        message={
          teacherToDelete
            ? teacherHasTimetableRecords
              ? `"${teacherToDelete.name}" has existing timetable records and will be deactivated instead of deleted. Historical data will be preserved.`
              : `Are you sure you want to delete ${teacherToDelete.name}? This action cannot be undone.`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setTeacherToDelete(null);
          setTeacherHasTimetableRecords(false);
        }}
        confirmLabel={teacherHasTimetableRecords ? 'Deactivate' : 'Delete'}
        cancelLabel="Cancel"
        severity="error"
      />
    </>
  );
}
